import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PlusIcon } from '@untitledui-icons/react/line'

import { Button } from '@/components/base/buttons'
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '@/components/ui/Modal'
import { LoadingSpinner } from '@/components/common'
import { getErrorMessage } from '@/lib/api'
import {
  useDeleteWidgetTemplate,
  useWidgetTemplates,
  type WidgetTemplate,
} from '@/hooks/api/useWidgets'
import { useCustomersByIds } from '@/hooks/api/useCustomers'
import { usePermissions } from '@/hooks/api/usePermissions'
import { WidgetTemplateCard } from './WidgetTemplateCard'

export function WidgetTemplatesTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const base = 'resources.widgets.templates'

  const { data: templates, isLoading } = useWidgetTemplates()
  const { isAdmin, isPartner } = usePermissions()
  const showCustomerContext = isAdmin || isPartner
  const customerIds = [...new Set((templates ?? []).map((template) => template.customer_id))]
  const { data: customers } = useCustomersByIds(customerIds, showCustomerContext)
  const customerNames = new Map(customers?.map((customer) => [customer.id, customer.name]))
  const deleteTemplate = useDeleteWidgetTemplate()
  const [pendingDeletion, setPendingDeletion] = useState<WidgetTemplate | null>(null)

  const handleDelete = async () => {
    if (!pendingDeletion || pendingDeletion.assignments.length > 0) return

    try {
      await deleteTemplate.mutateAsync(pendingDeletion.id)
      toast.success(t('resources.widgets.editor.deleted_toast'))
      setPendingDeletion(null)
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  const handleGoToAssignments = () => {
    if (!pendingDeletion) return
    navigate(`/widgets/templates/${pendingDeletion.id}`)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const items = templates ?? []
  const isAssigned = (pendingDeletion?.assignments.length ?? 0) > 0
  const assignedNames = [...(pendingDeletion?.assignments ?? [])]
    .map((assignment) => assignment.assistant_name)
    .sort((first, second) => first.localeCompare(second))
    .join(', ')

  return (
    <>
      <div className="grid w-full max-w-[1000px] gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <button
          type="button"
          onClick={() => navigate('/widgets/templates/new')}
          className="hover:border-brand-500 hover:text-brand-500 flex min-h-[124px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 p-5 text-sm font-medium text-gray-600 dark:border-gray-600 dark:text-gray-400"
        >
          <PlusIcon className="size-5" />
          {t(`${base}.new_template`)}
        </button>

        {items.map((template) => (
          <WidgetTemplateCard
            key={template.id}
            template={template}
            customerName={showCustomerContext ? customerNames.get(template.customer_id) : undefined}
            onDelete={() => setPendingDeletion(template)}
          />
        ))}
      </div>

      <Modal
        isOpen={!!pendingDeletion}
        onOpenChange={(isOpen) => !isOpen && setPendingDeletion(null)}
        size="md"
      >
        <ModalContent>
          <ModalHeader onClose={() => setPendingDeletion(null)} bordered={false}>
            <div className="space-y-1">
              <ModalTitle>
                {isAssigned
                  ? t(`${base}.cannot_delete_title`, { name: pendingDeletion?.name ?? '' })
                  : t(`${base}.delete_title`, { name: pendingDeletion?.name ?? '' })}
              </ModalTitle>
              <p className="text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
                {isAssigned
                  ? t(`${base}.cannot_delete_description`, {
                      count: pendingDeletion?.assignments.length ?? 0,
                      names: assignedNames,
                    })
                  : t(`${base}.delete_description_unused`)}
              </p>
            </div>
          </ModalHeader>

          <ModalFooter bordered={false} align="stretch">
            <Button color="secondary" size="lg" onPress={() => setPendingDeletion(null)}>
              {t('resources.generic.cancel')}
            </Button>
            {isAssigned ? (
              <Button color="primary" size="lg" onPress={handleGoToAssignments}>
                {t(`${base}.go_to_assignments`)}
              </Button>
            ) : (
              <Button
                color="primary-destructive"
                size="lg"
                isLoading={deleteTemplate.isPending}
                onPress={() => void handleDelete()}
              >
                {t(`${base}.delete_action`)}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
